/**
 * How the panel loads pictures, in one place.
 *
 * 1. Pictures on the brand's own website go through a service worker (public/image-sw.js)
 *    that fetches them without the website's cookies. Since the panel moved to Cloudflare
 *    Pages, the cookie-carrying requests for them were intermittently refused by Cloudflare
 *    (403/503) while cookie-less requests for the same files went through.
 * 2. A picture that still fails is tried again, 2 seconds to a minute apart, six times in all
 *    (about two and a half minutes), instead of staying broken until the page is reloaded.
 *    While it waits it carries data-retrying and is hidden (base.css), so its card shows an
 *    empty frame rather than a broken-image icon.
 *
 * Both cover every page at once: the worker sees every picture request and one capture
 * listener sees every <img> error, so no page has to opt in.
 */
import brand from "@/brand";

const RETRY_DELAYS = [2000, 5000, 10000, 20000, 40000, 60000];

// <img> -> { src, tries } while it waits for its next try.
const pending = new WeakMap();

export function installImageLoading() {
  registerPictureWorker(brand.website);
  document.addEventListener("error", onImageError, true);
  document.addEventListener("load", onImageLoad, true);
}

function registerPictureWorker(site) {
  if (!site || !("serviceWorker" in navigator)) return;
  const script = `${import.meta.env.BASE_URL}image-sw.js?site=${encodeURIComponent(site)}`;
  // Without the worker pictures load the usual way, so a failed registration only loses the fix.
  navigator.serviceWorker.register(script).catch(() => {});
}

function onImageError(event) {
  const img = event.target;
  if (!(img instanceof HTMLImageElement)) return;
  const src = img.getAttribute("src") || "";
  if (!src || /^(data|blob):/i.test(src)) return;

  const state = pending.get(img);
  const tries = state && state.src === src ? state.tries : 0;
  if (tries >= RETRY_DELAYS.length) {
    // Out of tries: let it show as broken.
    pending.delete(img);
    img.removeAttribute("data-retrying");
    return;
  }
  pending.set(img, { src, tries: tries + 1 });
  img.setAttribute("data-retrying", "");
  // Spread a little, so a page full of failed pictures does not retry in one burst.
  const delay = RETRY_DELAYS[tries] * (0.8 + Math.random() * 0.4);
  setTimeout(() => retry(img, src), delay);
}

function retry(img, src) {
  // Removed from the page, or showing another picture by now.
  if (!img.isConnected || img.getAttribute("src") !== src) return;
  img.removeAttribute("src");
  img.setAttribute("src", src);
}

function onImageLoad(event) {
  const img = event.target;
  if (!(img instanceof HTMLImageElement) || !img.hasAttribute("data-retrying")) return;
  pending.delete(img);
  img.removeAttribute("data-retrying");
}
